import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import useData from "../../hooks/useData";
import useBrowser from "../../hooks/useBrowser";
import usePage from "../../hooks/usePage";

import LocationAssignmentModal from "../modals/LocationAssignmentModal";

import Page from "./Page";
import EntityList from "./EntityList";
import Switch from "../elements/Switch";
import Date from "../elements/Date";
import Pagination from "../elements/Pagination";

const SelectedLocations = () => {
  const theme = useTheme();

  const location = useLocation();
  const navigate = useNavigate();
  const { windowHeight } = useBrowser();
  const { showMessage } = usePage();

  const { executeAction } = useData();

  const [locations, setLocations] = useState([]);
  const [selectionId, setSelectionId] = useState(null);
  const [pageNumber, setPageNumber] = useState(0);
  const [recordsPerPage, setRecordsPerPage] = useState(25);
  const [recordCount, setRecordCount] = useState(0);
  const [orderBy, setOrderBy] = useState("name");
  const [order, setOrder] = useState("asc");

  const [openAssignment, setOpenAssignment] = useState(false);

  const [syndicatedTerritories, setSyndicatedTerritories] = useState(false);
  const [customTerritories, setCustomTerritories] = useState(false);
  const [directOrders, setDirectOrders] = useState(false);
  const [frequency, setFrequency] = useState(false);
  const [dateRange, setDateRange] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [returnUrl, setReturnUrl] = useState(null);
  const [returnState, setReturnState] = useState(null);

  const userId = useSelector((state) => state.authentication.userId);
  const email = useSelector((state) => state.authentication.email);

  useEffect(() => {
    if (location?.state?.selectionId) {
      setSelectionId(location.state.selectionId);
    }
    if (location?.state?.returnUrl) {
      setReturnUrl(location.state.returnUrl);
    }
    if (location?.state?.returnState) {
      setReturnState(location.state.returnState);
    }
  }, []);

  // Dynamically size rows per page based on viewport height.
  useEffect(() => {
    if (!windowHeight) return;

    const CHROME_HEIGHT = 190; // matches tableHeight calculation below
    const HEADER_HEIGHT = 48;
    const ROW_HEIGHT = 44;

    const availableHeight = Math.max(200, windowHeight - CHROME_HEIGHT);
    const bodyHeight = Math.max(100, availableHeight - HEADER_HEIGHT);
    const computedRows = Math.max(
      10,
      Math.min(100, Math.floor(bodyHeight / ROW_HEIGHT)),
    );

    if (computedRows !== recordsPerPage) {
      setRecordsPerPage(computedRows);
      setPageNumber(0);
    }
  }, [windowHeight]);

  useEffect(() => {
    if (!selectionId) return;
    getData();
  }, [selectionId, pageNumber, recordsPerPage, orderBy, order]);

  const getData = async () => {
    const results = await executeAction({
      entityName: "WorkQueue",
      action: "getSelectionPage",
      data: {
        selectionId,
        pageNumber,
        recordsPerPage,
        orderBy,
        order,
      },
    });

    const data = results?.isSuccessful ? results.data : null;

    if (data?.records) {
      setLocations(data.records);
      setRecordCount(data.recordCount || 0);
    } else {
      setLocations([]);
      setRecordCount(0);
    }
  };

  const handleToolbarItemClick = async (action) => {
    switch (action) {
      case "back":
        if (location?.state?.locationsState) {
          navigate("/locations", { state: location.state.locationsState });
          break;
        }
        if (returnUrl) {
          navigate(returnUrl, { state: returnState });
        } else {
          navigate("/locations");
        }
        break;

      case "download":
        await submitWork();
        break;

      case "assign":
        setOpenAssignment(true);
        break;
    }
  };

  const submitWork = async () => {
    if (!selectionId) {
      showMessage("No selection found");
      return;
    }

    let options = {};

    if (syndicatedTerritories) options.syndicatedTerritories = true;
    if (customTerritories) options.customTerritories = true;
    if (directOrders) options.directOrders = true;
    if (frequency) options.frequency = true;
    if (dateRange) options.dateRange = true;
    if (fromDate) options.fromDate = fromDate;
    if (toDate) options.toDate = toDate;

    await executeAction({
      entityName: "WorkQueue",
      action: "setStatus",
      data: {
        id: selectionId,
        status: "waiting",
        options,
      },
      displayMessage: false,
    });

    showMessage("Your locations will be emailed to you shortly");

    // If coming from project context, navigate back to project after a brief delay
    if (returnUrl) {
      setTimeout(() => {
        navigate(returnUrl, { state: returnState });
      }, 1500);
    }
  };

  const handleAssigned = () => {
    setOpenAssignment(false);
    showMessage(
      "You will receive an email with your project locations shortly",
    );
  };

  const handleDateRange = (newValue) => {
    if (!newValue) {
      setFromDate("");
      setToDate("");
    }
    setDateRange(newValue);
  };

  const handlePageChange = (newPage) => {
    setPageNumber(newPage);
  };

  const handleRecordsPerPageChange = (newRecordsPerPage) => {
    setRecordsPerPage(newRecordsPerPage);
    setPageNumber(0);
  };

  const handleSortChange = (columnName, direction) => {
    setOrderBy(columnName);
    setOrder(direction);
  };

  return (
    <>
      <Page
        pageName="selectedLocations"
        includeToolbar
        items={[
          { name: "back", iconName: "ArrowBack" },
          { name: "download", iconName: "Download" },
        ]}
        onToolItemClick={handleToolbarItemClick}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            boxShadow: theme.shadows[4],
            padding: "8px",
            marginBottom: "10px",
            borderRadius: "6px",
          }}
          gap={1}
        >
          <Typography>Include: </Typography>

          <Switch
            label="Syndicated Territories"
            value={syndicatedTerritories}
            onChange={(newValue) => setSyndicatedTerritories(newValue)}
          />

          <Switch
            label="Custom Territory Column"
            value={customTerritories}
            onChange={(newValue) => setCustomTerritories(newValue)}
          />

          <Switch
            label="Include Direct Orders"
            value={directOrders}
            onChange={(newValue) => setDirectOrders(newValue)}
          />

          {!dateRange && (
            <Switch
              label="Frequency"
              value={frequency}
              onChange={(newValue) => setFrequency(newValue)}
            />
          )}

          {!frequency && (
            <Switch
              label="Date Range"
              value={dateRange}
              onChange={handleDateRange}
            />
          )}

          {dateRange && (
            <Date
              label="From"
              value={fromDate}
              onChange={(newValue) => setFromDate(newValue)}
            />
          )}

          {dateRange && (
            <Date
              label="To"
              value={toDate}
              onChange={(newValue) => setToDate(newValue)}
            />
          )}
        </Box>

        <Pagination
          totalCount={recordCount}
          styles={{ marginTop: "5px" }}
          pageNumber={pageNumber}
          recordsPerPage={recordsPerPage}
          onPageChange={handlePageChange}
          onRecordsPerPageChange={handleRecordsPerPageChange}
        />

        <EntityList
          entityList={locations}
          tableHeight={`${windowHeight - 190}px`}
          onSortChange={handleSortChange}
          orderBy={orderBy}
          order={order}
          columns={[
            { name: "banner", colIndex: 0 },
            { name: "name", colIndex: 1 },
            { name: "streetAddress", colIndex: 2 },
            { name: "city", colIndex: 3 },
            { name: "province", colIndex: 4 },
            { name: "postal", colIndex: 5 },
          ]}
        />
      </Page>

      {openAssignment && (
        <LocationAssignmentModal
          locations={locations}
          open={openAssignment}
          onClose={() => setOpenAssignment(false)}
          onAssigned={handleAssigned}
        />
      )}
    </>
  );
};

export default SelectedLocations;
